"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "../ui/OptimizedImage";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchAuthed } from "../../lib/api";
import { queryKeys, ENV_PREFIX } from "../../lib/query-keys";
import { scheduleIdle } from "../../lib/idle";
import { buildEventPath } from "../../lib/routes";
import { useMyEventIds } from "../../hooks/useMyEventIds";
import { EventPlaceholderCover } from "./EventPlaceholderCover";
import { AttendingBadge } from "./AttendingBadge";


type EventData = {
    id: string;
    eventId?: string;
    shortId?: string | null;
    title: string;
    date: string;
    imageUrl: string | null;
    hostName: string;
    locationName?: string | null;
    city?: string | null;
    category?: string | null;
};

type OnboardingStatus = {
    hasHostProfile: boolean;
};


const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date TBA";

    return date.toLocaleDateString("en-GB", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "Asia/Kolkata",
    });
};

// isUpcoming filter removed — API already filters with upcoming=true,
// and Date.now() in useMemo causes hydration mismatch with ISR

interface RecommendedEventsProps {
    initialData?: unknown[];
}

export function RecommendedEvents({ initialData }: RecommendedEventsProps) {
    const queryClient = useQueryClient();
    const { isSignedIn, isLoaded, getToken } = useAuth();
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [showGhostCard, setShowGhostCard] = useState(false);
    const myEventIds = useMyEventIds();
    // Wait for Clerk to resolve before switching to the authenticated query key.
    // While Clerk loads, use the public query with SSR initialData so the user
    // always sees events immediately instead of a flash of empty state.
    const authResolved = isLoaded && isSignedIn;
    const { data, isLoading } = useQuery({
        queryKey: queryKeys.events.recommended({ signedIn: authResolved }),
        queryFn: () => {
            if (authResolved) {
                return apiFetchAuthed<EventData[]>("events/recommended?limit=8", {}, () =>
                    getToken(),
                );
            }
            return apiFetch<EventData[]>("events?limit=8&upcoming=true&trending=true");
        },
        initialData: initialData as EventData[] | undefined,
        staleTime: 5 * 60_000,
    });
    const { data: onboardingStatus } = useQuery({
        queryKey: queryKeys.onboarding.status(),
        queryFn: () =>
            apiFetchAuthed<OnboardingStatus>("onboarding/status", {}, () => getToken()),
        enabled: isSignedIn,
        staleTime: 60_000,
    });

    const events = useMemo(
        () =>
            (data ?? [])
                .slice(0, 8)
                .map((entry) => ({
                    id: entry.id,
                    eventId: entry.eventId ?? entry.id,
                    shortId: entry.shortId,
                    title: entry.title,
                    date: formatDate(entry.date),
                    image: entry.imageUrl ?? null,
                    location: entry.locationName || entry.city || "Location TBA",
                    category: entry.category ?? null,
                })),
        [data],
    );
    const isHost = isSignedIn ? Boolean(onboardingStatus?.hasHostProfile) : null;
    const hostCta = useMemo(() => {
        if (!isSignedIn) {
            return { label: "Start hosting", href: `/auth?redirect=${encodeURIComponent("/onboarding?start=hostType")}` };
        }
        if (isHost === false) {
            return { label: "Become a host", href: "/onboarding?start=hostType" };
        }
        return { label: "Host one now", href: "/dashboard" };
    }, [isSignedIn, isHost]);
    const featureCta = {
        label: "Request featured placement",
        href: "mailto:info@hopp.life",
    };

    useEffect(() => {
        if (events.length === 0) {
            return;
        }
        return scheduleIdle(() => {
            events.forEach((event) => {
                queryClient.prefetchQuery({
                    queryKey: [ENV_PREFIX, "event", event.id],
                    queryFn: () => apiFetch<unknown>(`events/${event.id}`),
                    staleTime: 30_000,
                });
            });
        });
    }, [events, queryClient]);
    useEffect(() => {
        const element = scrollerRef.current;
        if (!element) {
            return;
        }
        const cardWidth = 160;
        const gap = 12;
        const measure = () => {
            if (events.length === 0) {
                setShowGhostCard(false);
                return;
            }
            const width = element.clientWidth;
            const contentWidth =
                events.length * cardWidth + Math.max(events.length - 1, 0) * gap;
            setShowGhostCard(contentWidth < width - 4);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [events.length]);

    const handleScroll = (direction: "left" | "right") => {
        const element = scrollerRef.current;
        if (!element) return;
        const amount = Math.max(200, Math.floor(element.clientWidth * 0.7));
        element.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };

    if (isLoading) {
        return (
            <section className="px-4 pt-3 pb-2 md:px-6 md:py-6" aria-busy="true" aria-label="Loading recommended events">
                <div className="mx-auto max-w-6xl">
                    <h2 className="mb-2 text-sm font-semibold text-[var(--hopp-text)] md:mb-4">
                        Recommended
                    </h2>
                    <div className="flex gap-3 overflow-hidden">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="w-40 flex-shrink-0 overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)]"
                            >
                                <div className="aspect-[4/3] animate-pulse bg-white/5" />
                                <div className="p-3">
                                    <div className="min-h-[2.5em] animate-pulse rounded bg-white/5" />
                                    <div className="mt-1 h-3 w-16 animate-pulse rounded bg-white/5" />
                                    <div className="mt-2 h-4 w-20 animate-pulse rounded bg-white/5" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (events.length === 0) {
        return (
        <section className="px-4 pt-3 pb-2 md:px-6 md:py-6">
                <div className="mx-auto max-w-6xl">
                    <h2 className="mb-2 text-sm font-semibold text-[var(--hopp-text)] md:mb-4">
                        Recommended
                    </h2>
                    <div className="rounded-2xl bg-[var(--hopp-panel)] px-6 py-8 text-center">
                        <p className="text-sm font-semibold text-[var(--hopp-text)]">
                            No events for you yet.
                        </p>
                        <div className="mt-4 flex flex-wrap justify-center gap-3">
                            <Link
                                href={featureCta.href}
                                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-md shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] hover:bg-white/15 hover:border-white/30 px-5 py-2 text-xs font-semibold text-[#EDEDED] transition hover:opacity-90"
                            >
                                {featureCta.label}
                            </Link>
                            {isHost === false || !isSignedIn ? (
                                <Link
                                    href={hostCta.href}
                                    className="inline-flex items-center justify-center rounded-full border border-[var(--hopp-panel-strong)] px-5 py-2 text-xs font-semibold text-[var(--hopp-text)] transition hover:opacity-90"
                                >
                                    {hostCta.label}
                                </Link>
                            ) : null}
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="px-4 pt-3 pb-2 md:px-6 md:py-6" aria-live="polite">
            <div className="mx-auto max-w-6xl">
                {/* Section Header */}
                <h2 className="mb-2 text-sm font-semibold text-[var(--hopp-text)] md:mb-4">
                    Recommended
                </h2>

                {/* Horizontal Scroll Container */}
                <div
                    ref={scrollerRef}
                    tabIndex={0}
                    role="region"
                    aria-label="Events for you carousel"
                    onKeyDown={(e) => {
                        if (e.key === "ArrowLeft") handleScroll("left");
                        if (e.key === "ArrowRight") handleScroll("right");
                    }}
                    className="scrollbar-hide flex gap-3 overflow-x-auto px-1 pb-1 sm:px-0 sm:pb-2 snap-x snap-mandatory focus:outline-none focus-visible:ring-1 focus-visible:ring-white/20"
                >
                    {events.map((event) => (
                        <Link
                            key={event.id}
                            href={buildEventPath(event.shortId ?? event.id, event.title)}
                            className="flex-shrink-0 snap-start"
                        >
                            <div
                                className="group w-40 overflow-hidden rounded-xl border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] transition-transform duration-150 ease-out hover:-translate-y-0.5"
                            >
                                {/* Image */}
                                <div className="relative aspect-[4/3] overflow-hidden">
                                    {event.image ? (
                                        <Image
                                            src={event.image}
                                            alt={event.title}
                                            fill
                                            sizes="160px"
                                            className="object-cover transition-transform duration-150 group-hover:scale-105"
                                        />
                                    ) : (
                                        <EventPlaceholderCover
                                            title={event.title}
                                            category={event.category}
                                            size="sm"
                                        />
                                    )}
                                    {myEventIds.has(event.eventId) ? <AttendingBadge /> : null}
                                </div>

                                {/* Content */}
                                <div className="p-3">
                                    <h3 className="line-clamp-2 min-h-[2.5em] text-xs font-semibold leading-snug text-[var(--hopp-text)]">
                                        {event.title}
                                    </h3>
                                    <p className="mt-1 text-[10px] text-[var(--hopp-muted)]">
                                        {event.date}
                                    </p>
                                    <span className="mt-2 inline-block max-w-full truncate whitespace-nowrap rounded bg-[var(--hopp-panel-strong)] px-1.5 py-0.5 text-[9px] text-[var(--hopp-muted)]">
                                        {event.location}
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                    {showGhostCard ? (
                        <Link
                            href={isHost === false || !isSignedIn ? hostCta.href : featureCta.href}
                            className="flex-shrink-0 snap-start"
                            aria-label={
                                isHost === false || !isSignedIn
                                    ? hostCta.label
                                    : featureCta.label
                            }
                        >
                            <div
                                className="group relative w-40 overflow-hidden rounded-xl border border-dotted border-[var(--hopp-panel-strong)] bg-transparent transition-transform duration-150 ease-out hover:-translate-y-0.5"
                            >
                                <div className="opacity-0">
                                    <div className="aspect-[4/3]" />
                                    <div className="p-3">
                                        <h3 className="line-clamp-2 min-h-[2.5em] text-xs font-semibold leading-snug">
                                            Placeholder
                                        </h3>
                                        <p className="mt-1 text-[10px]">Date</p>
                                        <span className="mt-2 inline-block rounded px-1.5 py-0.5 text-[9px]">
                                            Location
                                        </span>
                                    </div>
                                </div>
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full border border-dotted border-[var(--hopp-panel-strong)] text-2xl text-[var(--hopp-text)]">
                                        +
                                    </div>
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[var(--hopp-text)]">
                                        {isHost === false || !isSignedIn ? hostCta.label : featureCta.label}
                                    </span>
                                    {isHost === false || !isSignedIn ? null : (
                                        <span className="text-[9px] uppercase tracking-[0.25em] text-[var(--hopp-muted)]">
                                            info@hopp.life
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Link>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
